// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CorrectionLetterModal/index.jsx
// *******************************************************************************
// CORRECTION LETTER MODAL - Carta de Correção (CCe) para NF-e
// NOTE: Disponível apenas para NF-e. NFC-e não suporta carta de correção na NuvemFiscal.
// *******************************************************************************
import { useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { createRoot } from 'react-dom/client';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import correctionLetterModalStep from './Step1';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CorrectionLetterModal = ({
  show,
  setShow,
  afterClose,
  company,
  invoiceId,
  invoiceNumber,
  isNfce,
  onSuccess,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [resultData, setResultData] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      correctionLetterModalStep({ company, invoiceId, invoiceNumber, resultData, showResult, isNfce }),
    ],
    [company, invoiceId, invoiceNumber, resultData, showResult, isNfce]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, setLoading }) => {
    if (showResult || isNfce) {
      return true;
    }

    const values = form?.getFieldsValue(true) || {};
    const companyId = company?._id || company?.entityId;
    const finalInvoiceId = invoiceId || values.invoiceId;

    // Build corrections array from form values
    const corrections = (values.corrections || []).map(c => ({
      grupo_alterado: c.grupo_alterado,
      campo_alterado: c.campo_alterado,
      valor_alterado: c.valor_alterado,
    }));

    const payload = {
      companyId,
      invoiceId: finalInvoiceId,
      corrections,
    };

    setLoading(true);

    try {
      const response = await axiosService.post({
        url: API.FISCAL.NFCE_CORRECTION_LETTER,
        data: payload,
      });

      console.log('[CorrectionLetterModal] Full response:', response);

      // Handle axiosService response format (can be array or object)
      let responseData;
      if (Array.isArray(response)) {
        // axiosService returns [data, summary] when returnAsObject is used
        responseData = response[0];
      } else {
        responseData = response;
      }

      console.log('[CorrectionLetterModal] Response data:', responseData);

      // Check success at root level (routerService wraps response)
      const isSuccess = responseData?.success === true;
      const hasData = responseData?.data?.success === true;

      console.log('[CorrectionLetterModal] Success check:', { isSuccess, hasData });

      if (isSuccess || hasData) {
        // Get data from either root or data property
        const successData = hasData ? responseData.data : responseData;

        const result = {
          success: true,
          protocolNumber: successData.protocolNumber || successData.protocol,
          cceId: successData.id || successData.cceId,
          eventDate: successData.eventDate || successData.data_evento,
          invoiceId: finalInvoiceId,
          corrections,
          xml: successData.xml,
          xmlStorageUrl: successData.xmlStorageUrl,
        };

        setResultData(result);
        setShowResult(true);
        onSuccess?.(result);

        return false;
      } else {
        // Get error details from response
        const errorDetails = responseData?.raw || responseData;

        // Build meaningful error message
        let errorMsg = translateX('correction_letter_error');
        let errorCode = null;

        // Check for specific SEFAZ errors (following VoidNfceModal pattern)
        if (responseData?.errors?.[0]?.error) {
          errorMsg = responseData.errors[0].error;
          errorCode = responseData.errors[0].code;
        } else if (errorDetails?.motivo_status) {
          // NuvemFiscal rejection
          errorMsg = errorDetails.motivo_status;
          errorCode = errorDetails.codigo_status;
        } else if (errorDetails?.errors?.[0]?.message) {
          errorMsg = errorDetails.errors[0].message;
        } else if (errorDetails?.error?.message) {
          errorMsg = errorDetails.error.message;
        } else if (responseData?.message) {
          errorMsg = responseData.message;
        } else if (responseData?.error) {
          errorMsg = responseData.error;
        }

        console.log('[CorrectionLetterModal] Error details:', {
          responseData,
          errorDetails,
          errorMsg,
          errorCode,
        });

        setResultData({
          success: false,
          error: errorMsg,
          code: errorCode,
          xml: responseData?.xml,
          xmlStorageUrl: responseData?.xmlStorageUrl,
        });
        setShowResult(true);

        return false;
      }
    } catch (error) {
      console.error('[CorrectionLetterModal] Error:', error);

      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        translateX('correction_letter_error');

      setResultData({
        success: false,
        error: errorMsg,
      });
      setShowResult(true);

      return false;
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={showResult ? 'correction_letter_result' : 'correction_letter_title'}
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={600}
      maskClosable={false}
      nextButtonText={showResult || isNfce ? 'close' : 'send_correction_letter'}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION
// *******************************************************************************
const showCorrectionLetterModal = ({ company, invoiceId, invoiceNumber, isNfce, onSuccess } = {}) => {
  const container = document.createElement('div');
  container.id = 'correction-letter-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);
  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <CorrectionLetterModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          company={company}
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          isNfce={isNfce}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default showCorrectionLetterModal;
export { CorrectionLetterModal };
